/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import FsiPicker from '~/components/FsiPicker/FsiPicker'
import { File } from '~/constants/file'
import { useEsignature } from '~/hooks/useEsignature'
import { useTranslation } from 'react-i18next'
import {
  moveFile,
  moveMultipleFiles,
  moveAllFilesInFolder,
  moveAllFilesFromPropertiesSearch,
  copyFile,
  copyMultipleFiles,
  copyAllFilesInFolder,
  copyAllFilesFromPropertiesSearch
} from '~/services/api/files'

interface MoveAndCopyModalProps {
  fileDetails: any
  options?: any
  onClose?: () => void
}

const MoveAndCopyModal: React.FC<MoveAndCopyModalProps> = ({ fileDetails, options = {}, onClose }) => {
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { $rootScope } = context
  const { translate } = context.service.filter
  const { t } = useTranslation()

  // Local excerpt function - not available in service.filter
  const excerpt = (text: string, stripHtml: boolean, length: number) => {
    if (!text) return ''
    const truncated = text.length > length ? text.substring(0, length) + '...' : text
    return stripHtml ? truncated.replace(/<[^>]*>/g, '') : truncated
  }
  const { signAction } = useEsignature()
  const [isOpen, setIsOpen] = useState(false)
  const [fileId, setFileId] = useState<any>(null)
  const [fileDetailsState, setFileDetailsState] = useState<any>({})

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const loadData = () => {
    if (fileDetails?.id) {
      setFileId(fileDetails.id)
      if (fileDetails.path && fileDetails.path.length >= 2) {
        fileDetails.parent = fileDetails.path[fileDetails.path.length - 2]
        setFileDetailsState(fileDetails)
      } else if (fileDetails.parentId || options.parentFolderId) {
        // load parent fsi to get some required data
        context.api.files
          .getFile(fileDetails.parentId || options.parentFolderId)
          .then(function (data: any) {
            fileDetails.parent = data
            setFileDetailsState(fileDetails)
          })
          .catch(function () {
            fileDetails.parent = {}
            setFileDetailsState(fileDetails)
          })
      }
    } else {
      doClose()
    }
  }

  const move = (values: any) => {
    doClose()

    const doMoveCallback = async function (validation?: any) {
      if (options.selectedIds && options.selectedIds.length > 1) {
        setFileId(options.selectedIds)
      }

      const newParentId = values?.folderId?.id || auth.getBrowser(File.systemFolderType.REGULATED_CONTENT).id
      const retainAcls = values.retainAcls

      let apiCall: Promise<any> | undefined

      try {
        if (options.selectedWholeResult) {
          // Bulk operation - move all files in result set
          if (options.isPropertiesSearch && options.filter && options.filter.metadataCriteria) {
            // Properties search bulk move
            const filter = { ...options.filter, workflowIsNull: true }
            apiCall = moveAllFilesFromPropertiesSearch({
              propertySearchRequestDTO: { filter },
              operationDTO: {
                newParentId,
                retainAcls,
                esignature: validation
              }
            })
          } else if (options.parentFolderId) {
            // Folder bulk move
            apiCall = moveAllFilesInFolder(options.parentFolderId, {
              newParentId,
              retainAcls,
              esignature: validation,
              filter: options.filter
            })
          } else {
            throw new Error('Invalid bulk move operation configuration')
          }
        } else {
          // Single or multi-file move
          const fileIds = fileDetails.type === File.type.FOLDER || !options?.selectedIds ? fileId : options.selectedIds

          if (Array.isArray(fileIds) && fileIds.length > 1) {
            // Multi-file move
            apiCall = moveMultipleFiles({
              ids: fileIds,
              newParentId,
              retainAcls,
              esignature: validation
            })
          } else {
            // Single file move
            apiCall = moveFile(Array.isArray(fileIds) ? fileIds[0] : fileIds, {
              newParentId: newParentId,
              retainAcls,
              esignature: validation
            })
          }
        }

        if (!apiCall) {
          throw new Error('No API call was initiated')
        }

        await apiCall
        // Trigger table refresh (window event bus)
        window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'MOVE_FSI' }))
        toast.success(translate('BROWSER_MOVE_MSG_SUCCESS'))
      } catch (error: any) {
        toast.error(translate('ERROR_ERROR', { error: error?.message || error?.data?.message }))
        throw error
      }

      return apiCall!
    }

    if (auth.requireESignatureForFileCommands()) {
      return signAction('ADMIN_ACTION', 'ADMIN_ACTION_MOVE', { keepParentModalsOpen: false }).then(
        (validation: any) => {
          return doMoveCallback(validation)
        }
      )
    } else {
      return doMoveCallback()
    }
  }

  const copy = (values: any) => {
    doClose()

    const doCopyCallback = async function (validation?: any) {
      if (options.selectedIds && options.selectedIds.length > 1) {
        setFileId(options.selectedIds)
      }

      const newParentId = values?.folderId?.id || auth.getBrowser(File.systemFolderType.REGULATED_CONTENT).id
      const retainAcls = values.retainAcls

      let apiCall: Promise<any> | undefined

      try {
        if (options.selectedWholeResult) {
          // Bulk operation - copy all files in result set
          if (options.isPropertiesSearch && options.filter && options.filter.metadataCriteria) {
            // Properties search bulk copy
            const filter = { ...options.filter, workflowIsNull: true }
            apiCall = copyAllFilesFromPropertiesSearch({
              propertySearchRequestDTO: { filter },
              operationDTO: {
                newParentId,
                retainAcls,
                esignature: validation
              }
            })
          } else if (options.parentFolderId) {
            // Folder bulk copy
            apiCall = copyAllFilesInFolder(options.parentFolderId, {
              newParentId,
              retainAcls,
              esignature: validation,
              filter: options.filter
            })
          } else {
            throw new Error('Invalid bulk copy operation configuration')
          }
        } else {
          // Single or multi-file copy
          const fileIds = fileDetails.type === File.type.FOLDER || !options?.selectedIds ? fileId : options.selectedIds

          if (Array.isArray(fileIds) && fileIds.length > 1) {
            // Multi-file copy
            apiCall = copyMultipleFiles({
              ids: fileIds,
              newParentId,
              retainAcls,
              esignature: validation
            })
          } else {
            // Single file copy
            apiCall = copyFile(Array.isArray(fileIds) ? fileIds[0] : fileIds, {
              newParentId: newParentId,
              retainAcls,
              esignature: validation
            })
          }
        }

        if (!apiCall) {
          throw new Error('No API call was initiated')
        }

        await apiCall
        if ($rootScope) {
          $rootScope.$emit('updateFiles', 'COPY_FSI')
        }
        toast.success(translate('BROWSER_COPY_MSG_SUCCESS'))
      } catch (error: any) {
        toast.error(translate('ERROR_ERROR', { error: error?.message || error?.data?.message }))
        throw error
      }

      return apiCall!
    }

    if (auth.requireESignatureForFileCommands()) {
      return signAction('ADMIN_ACTION', 'ADMIN_ACTION_COPY', { keepParentModalsOpen: false }).then(
        (validation: any) => {
          return doCopyCallback(validation)
        }
      )
    } else {
      return doCopyCallback()
    }
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        innerRef={(el: any) => {
          context.store.formRef = el
        }}
        initialValues={{ ...fileDetails, retainAcls: false }}
        onSubmit={() => {}}
      >
        {({ values, setFieldValue }) => (
          <Modal
            show={isOpen}
            handleClose={doClose}
            className="move-copy-modal"
            title={
              options.selectedWholeResult || (options.canMove && options.canCopy)
                ? translate('BROWSER_MOVE_COPY_ITEM')
                : options.canMove
                  ? translate('BROWSER_MOVE_ITEM')
                  : translate('BROWSER_COPY_ITEM')
            }
          >
            <Modal.Body>
              <div className="form-group md-form-group wparag w100p">
                <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
                  {fileDetails.type === File.type.FOLDER
                    ? translate('FSI_TYPE_FOLDER')
                    : translate('FSI_TYPE_DOCUMENT')}
                </Form.Label>
                {(options.selectedWholeResult || (options.selectedIds && options.selectedIds.length > 1)) && (
                  <p className="whitespaceprewrap">{translate('WORKFLOW_MULTIPLE_DOCS')}</p>
                )}
                {!options.selectedWholeResult && !options.selectedIds && (
                  <p className="whitespaceprewrap">{fileDetails.name}</p>
                )}
              </div>

              <div className="form-group md-form-group wparag w100p">
                <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>{translate('EXISTING_FOLDER')}</Form.Label>
                <p title={fileDetails?.parent?.name}>{excerpt(fileDetails?.parent?.name, false, 50)}</p>
              </div>

              <div className="form-group md-form-group wparag w100p">
                <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>{translate('NEW_FOLDER')}</Form.Label>
                <p title={values?.folderId?.name}>
                  {values?.folderId?.name ? excerpt(values?.folderId?.name, false, 50) : translate('NOT_AVAILABLE')}
                </p>
              </div>

              <div>
                <FsiPicker
                  options={{
                    selectedId: values?.folderId?.id || null,
                    leafIds: undefined,
                    showRC: true,
                    showSystem: true,
                    selector: 'folder'
                  }}
                  onSelect={(fsi: any) => {
                    if (fsi && fsi.id) {
                      setFieldValue('folderId', { id: fsi.id, name: fsi.name })
                    }
                  }}
                />
              </div>

              <div>
                <Form.Group controlId="retain-acls-checkbox">
                  <Form.Check
                    type="checkbox"
                    name="retainAcls"
                    checked={values.retainAcls}
                    onChange={(e) => setFieldValue('retainAcls', e.target.checked)}
                    label={translate('BROWSER_MOVECOPY_RETAIN_ACL')}
                    id="retain-acls-checkbox"
                    className="retain-acls-checkbox"
                    style={{ fontSize: '12px' }}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button data-action={'ok'} variant="link" onClick={doClose} id="cancel-button">
                {translate('CANCEL')}
              </Button>
              {(options.canMove || options.selectedWholeResult) && (
                <Button data-action={'ok'} className="btn btn-primary" onClick={() => move(values)} id="move-button">
                  {translate('ADMIN_ACTION_MOVE')}
                </Button>
              )}
              {(options.canCopy || options.selectedWholeResult) && (
                <Button data-action={'ok'} className="btn btn-primary" onClick={() => copy(values)} id="copy-button">
                  {translate('ADMIN_ACTION_COPY')}
                </Button>
              )}
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    </>
  )
}

export default MoveAndCopyModal
